import { useState } from "react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Plus, Wine, ChevronRight, ChevronLeft, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useOnDeckAssignments } from "@/hooks/useOnDeckAssignments";
import type { VesselAssignmentWithRelations } from "@/hooks/useVesselAssignments";
import { getLotForAssignment } from "@/hooks/useVesselAssignments";
import { PlanLotDialog } from "@/components/tank-planning/PlanLotDialog";
import { useVolumeUnit } from "@/hooks/useOrganization";

interface OnDeckSidebarProps {
  expanded: boolean;
  onToggle: () => void;
  isDraggingAssignment?: boolean;
  /**
   * Customer portal: the client's own On Deck lots (from get_client_tank_schedule).
   * When provided, the sidebar shows these instead of querying vessel_assignment.
   */
  assignments?: VesselAssignmentWithRelations[];
  isLoading?: boolean;
  /** Customer portal: no "Add Lot" button and lots can't be dragged. */
  readOnly?: boolean;
}

export function OnDeckSidebar({
  expanded,
  onToggle,
  isDraggingAssignment = false,
  assignments: providedAssignments,
  isLoading: providedLoading,
  readOnly = false,
}: OnDeckSidebarProps) {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const onDeckQuery = useOnDeckAssignments({ enabled: !providedAssignments });
  const assignments = providedAssignments ?? onDeckQuery.data ?? [];
  const isLoading = providedAssignments ? !!providedLoading : onDeckQuery.isLoading;

  const { isOver, setNodeRef: setDropRef } = useDroppable({
    id: "on-deck-sidebar",
    data: { type: "on-deck-drop" },
  });

  return (
    <>
      <div
        ref={setDropRef}
        className={cn(
          "flex-shrink-0 border-l bg-muted/30 transition-all duration-300 flex flex-col",
          expanded || isDraggingAssignment ? "w-56" : "w-10",
          isOver && "bg-primary/10 border-l-2 border-l-primary ring-2 ring-inset ring-primary/50",
          isDraggingAssignment && !isOver && "bg-amber-50 dark:bg-amber-950/20 border-l-2 border-l-amber-400 border-dashed"
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-2 border-b bg-background/50">
          {expanded && (
            <div className="flex items-center gap-2 min-w-0">
              <Wine className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="font-medium text-sm truncate">On Deck</span>
              <span className="text-xs text-muted-foreground">({assignments.length})</span>
            </div>
          )}
          <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0" onClick={onToggle}>
            {expanded ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>

        {/* Collapsed state */}
        {!expanded && !isDraggingAssignment && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className="flex flex-col items-center gap-1.5 p-2 w-full hover:bg-muted/50 transition-colors"
                  onClick={onToggle}
                >
                  <Wine className="h-3.5 w-3.5 text-muted-foreground" />
                  {assignments.length > 0 && (
                    <span className="text-[10px] font-bold bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center leading-none">
                      {assignments.length}
                    </span>
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>On Deck — {assignments.length} lot{assignments.length !== 1 ? 's' : ''} (click to expand)</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {/* Drop zone indicator when dragging an assignment */}
        {isDraggingAssignment && (
          <div className={cn(
            "flex-1 flex flex-col items-center justify-center p-4 text-center",
            isOver ? "bg-primary/10" : "bg-amber-50/50 dark:bg-amber-950/10"
          )}>
            <div className={cn(
              "rounded-xl border-2 border-dashed p-6 w-full transition-colors",
              isOver ? "border-primary bg-primary/5" : "border-amber-400 dark:border-amber-600"
            )}>
              <Wine className={cn("h-8 w-8 mx-auto mb-2", isOver ? "text-primary" : "text-amber-500")} />
              <p className={cn("text-sm font-medium", isOver ? "text-primary" : "text-amber-700 dark:text-amber-400")}>
                {isOver ? "Release to unassign" : "Drop here to unassign"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Lot will be moved to On Deck</p>
            </div>
          </div>
        )}

        {/* Expanded list */}
        {expanded && !isDraggingAssignment && (
          <>
            {!readOnly && (
            <div className="p-2 border-b">
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() => setCreateDialogOpen(true)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Lot
              </Button>
            </div>
            )}
            <ScrollArea className="flex-1">
              {isLoading ? (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : assignments.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  <p>No lots on deck</p>
                  <p className="text-xs mt-1">
                    {readOnly ? "Lots waiting for a tank appear here" : "Add lots to plan tank assignments"}
                  </p>
                </div>
              ) : (
                <div className="p-2 space-y-1">
                  {assignments.map((assignment) => (
                    <DraggableAssignmentItem key={assignment.id} assignment={assignment} readOnly={readOnly} />
                  ))}
                </div>
              )}
            </ScrollArea>
          </>
        )}
      </div>

      {!readOnly && (
        <PlanLotDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
        />
      )}
    </>
  );
}

function DraggableAssignmentItem({
  assignment,
  readOnly = false,
}: {
  assignment: VesselAssignmentWithRelations;
  readOnly?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `on-deck-${assignment.id}`,
    data: { type: "on-deck-lot", assignment },
    disabled: readOnly,
  });
  const { volumeAbbrev } = useVolumeUnit();

  const lot = getLotForAssignment(assignment);
  const volume = assignment.fill_volume_l ?? lot?.current_volume_l;

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform) }}
      className={cn(
        "flex items-center justify-between px-2 py-1.5 rounded-md border bg-background",
        readOnly ? "cursor-default" : "cursor-grab hover:border-primary/50 hover:bg-accent/50 transition-colors",
        isDragging && "opacity-50 cursor-grabbing shadow-lg"
      )}
      {...listeners}
      {...attributes}
    >
      <span className="font-medium text-sm truncate">{assignment.display_label ?? lot?.code ?? "Unknown"}</span>
      {volume && (
        <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">
          {Math.round(volume).toLocaleString()} {volumeAbbrev}
        </span>
      )}
    </div>
  );
}
